import clsx from "clsx";
import { DateFormat, formatDate, isGiftOfferProduct } from "common";
import { Dayjs } from "dayjs";
import React, { Fragment, ReactNode } from "react";
import { BaseSchedule, Product, Schedule } from "types";
import { ExpandableList } from "@/components/Common/ExpandableList";
import { getScheduleSummary } from "@/helpers/scheduleSummaryHelper";
import { useResource } from "@/hooks/useResource";

export interface PurchaseRuleTileProps {
    title: string;
    className?: string;
    children: ReactNode;
}

export const PurchaseRuleTile = ({ title, className, children }: PurchaseRuleTileProps) => {
    return (
        <div
            className={clsx("flex-1 space-y-3 rounded bg-gray-lighter py-5 px-6", className)}
            data-testid="create-purchase-gift-restrictions-purchase-rule-tile"
        >
            <div className="flex flex-col">
                <div className="font-bold">{title}</div>
                <div className="flex flex-row space-x-1">{children}</div>
            </div>
        </div>
    );
};

export const DateRange = ({ start, end }: { start: string | Dayjs | null; end: string | Dayjs | null }) => (
    <div className="flex flex-col">
        <div>Start Date: {start ? formatDate(start, DateFormat.DATE_LONG) : "No start"}</div>
        <div>End Date: {end ? formatDate(end, DateFormat.DATE_LONG) : "No end"}</div>
    </div>
);

export const ProductName = ({ id }: { id: ID }) => {
    const { data: product, isLoading } = useResource<Product>(["products", id]);

    if (isLoading) {
        return null;
    }

    const productName = () => {
        if (!product?.name) return "N/A";

        if (isGiftOfferProduct(product)) {
            return product.baseGiftProduct
                ? "Custom Amount Gift"
                : product.experience
                ? `Gift towards ${product.name}`
                : product.name;
        }

        return product.name;
    };

    return <div className="leading-6">{productName()}</div>;
};

export const ProductRestrictionsList = ({
    productRestrictions,
    type = "list",
}: {
    productRestrictions: string[] | null;
    type?: "text" | "list";
}) => {
    const productList = productRestrictions ?? [];
    const numberOfProducts = productList.length;

    if (numberOfProducts === 0) {
        return <span>All Experiences</span>;
    }

    if (type === "text") {
        return (
            <div className="mt-1 flex flex-wrap">
                {productList.map<ReactNode>((id, index) => (
                    <Fragment key={id}>
                        <ProductName key={id} id={id} />
                        {index !== productList.length - 1 && <span>,&nbsp;</span>}
                    </Fragment>
                ))}
            </div>
        );
    }

    return (
        <div className="mt-1 flex w-full flex-col items-start">
            <ExpandableList
                styles={{
                    button: { className: "mt-2", size: "medium" },
                }}
                entities={productList}
                component={(id) => <ProductName key={id} id={id} />}
            />
        </div>
    );
};

interface ScheduleRestrictionsProps {
    available: BaseSchedule[];
    blackout: BaseSchedule[];
}

export const ScheduleRestrictions = ({ available, blackout }: ScheduleRestrictionsProps) => {
    const schedules = [...available, ...blackout];

    if (schedules.length === 0) {
        return <span>None</span>;
    }

    return (
        <ul className="list-disc pl-4">
            {schedules.map((schedule, index) => (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index}>{getScheduleSummary(schedule as Schedule)}</li>
            ))}
        </ul>
    );
};
